import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsScript } from '../../../utils/googleMapsLoader';
import { EventListItem } from '../../../types';
import { calculateMapBounds, getIstanbulCoordinates } from '../../../utils/mapUtils';
import { generateEventUrl } from '../../../utils/urlSlug';
import styles from './EventsMap.module.css';

interface EventsMapProps {
  events: EventListItem[];
  isOnlineFilterActive: boolean;
}

// Type for extended Google Maps API with LatLngBounds and Size
interface ExtendedGoogleMaps {
  Map: new (element: HTMLElement, options: any) => any;
  Marker: new (options: any) => any;
  LatLng: new (lat: number, lng: number) => any;
  LatLngBounds: new () => any;
  Size: new (width: number, height: number) => any;
  Geocoder: new () => any;
  places?: {
    Autocomplete: new (input: HTMLInputElement, options?: any) => any;
  };
}

// Helper function to create purple circle marker icon
const createPurpleCircleIcon = (maps: ExtendedGoogleMaps) => {
  const circleSvg = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
    <svg width="20" height="20" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="8" fill="#8B5CF6" stroke="#FFFFFF" stroke-width="2"/>
    </svg>
  `);
  
  return {
    url: circleSvg,
    scaledSize: new maps.Size(20, 20),
  };
};

const EventsMap: React.FC<EventsMapProps> = ({ events, isOnlineFilterActive }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const isInitialized = useRef(false);

  // Filter events with valid coordinates (exclude online events)
  const eventsWithCoords = events.filter((event) => {
    if (event.isOnline === "1") return false;
    if (!event.locationLatitude || !event.locationLongitude) return false;
    
    const lat = parseFloat(event.locationLatitude);
    const lng = parseFloat(event.locationLongitude);
    
    return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
  });

  // Load Google Maps script
  useEffect(() => {
    loadGoogleMapsScript([])
      .then(() => {
        setScriptLoaded(true);
      })
      .catch((error: Error) => {
        setLoadError(error.message || 'Failed to load Google Maps');
      });
  }, []);

  // Initialize map and markers
  useEffect(() => {
    if (!scriptLoaded || !mapRef.current) {
      return;
    }

    if (!window.google || !window.google.maps) {
      return;
    }

    const initMap = () => {
      if (!mapRef.current || !window.google?.maps) {
        return;
      }

      try {
        // Calculate bounds from events
        const bounds = calculateMapBounds(eventsWithCoords);
        
        // Dark mode style configuration
        const darkModeStyles = [
          { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
          {
            featureType: 'administrative.locality',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#d59563' }]
          },
          {
            featureType: 'poi',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#d59563' }]
          },
          {
            featureType: 'poi.park',
            elementType: 'geometry',
            stylers: [{ color: '#263c3f' }]
          },
          {
            featureType: 'poi.park',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#6b9a76' }]
          },
          {
            featureType: 'road',
            elementType: 'geometry',
            stylers: [{ color: '#38414e' }]
          },
          {
            featureType: 'road',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#212a37' }]
          },
          {
            featureType: 'road',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#9ca5b3' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'geometry',
            stylers: [{ color: '#746855' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#1f2835' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#f3d19c' }]
          },
          {
            featureType: 'transit',
            elementType: 'geometry',
            stylers: [{ color: '#2f3948' }]
          },
          {
            featureType: 'transit.station',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#d59563' }]
          },
          {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [{ color: '#17263c' }]
          },
          {
            featureType: 'water',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#515c6d' }]
          },
          {
            featureType: 'water',
            elementType: 'labels.text.stroke',
            stylers: [{ color: '#17263c' }]
          }
        ];

        // Initialize map - use type assertion for extended API
        const maps = window.google.maps as unknown as ExtendedGoogleMaps;
        mapInstance.current = new maps.Map(mapRef.current, {
          center: bounds.center,
          zoom: bounds.zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          panControl: false,
          rotateControl: false,
          scaleControl: false,
          disableDefaultUI: true,
          keyboardShortcuts: false,
          clickableIcons: false,
          gestureHandling: 'auto',
          styles: darkModeStyles,
        });

        // Create bounds object to fit all markers
        const mapBounds = new maps.LatLngBounds();

        // Clear existing markers
        markersRef.current.forEach((marker) => {
          marker.setMap(null);
        });
        markersRef.current = [];

        // Create markers for each event
        eventsWithCoords.forEach((event) => {
          const lat = parseFloat(event.locationLatitude!);
          const lng = parseFloat(event.locationLongitude!);
          const position = { lat, lng };

          // Add to bounds
          mapBounds.extend(position);

          // Create marker with custom purple circle icon
          const marker = new maps.Marker({
            map: mapInstance.current,
            position: position,
            icon: createPurpleCircleIcon(maps),
            title: event.eventTitle || event.title || 'Event',
          });

          // Add click listener to open event in new tab
          marker.addListener('click', () => {
            const eventUrl = generateEventUrl(event.eventTitle || event.title, event.eventID);
            window.open(eventUrl, '_blank');
          });

          markersRef.current.push(marker);
        });

        // Fit bounds to show all markers (if we have any)
        if (eventsWithCoords.length > 0) {
          mapInstance.current.fitBounds(mapBounds);
          // Add padding to bounds
          mapInstance.current.setOptions({
            zoom: mapInstance.current.getZoom()! > 15 ? 15 : mapInstance.current.getZoom(),
          });
        }

        isInitialized.current = true;
      } catch (error) {
        setLoadError('Failed to initialize map');
      }
    };

    // Small delay to ensure API is fully ready
    const timer = setTimeout(() => {
      initMap();
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [scriptLoaded, eventsWithCoords]);

  // Update markers when events change
  useEffect(() => {
    if (!isInitialized.current || !mapInstance.current || !window.google?.maps) {
      return;
    }

    // Clear existing markers
    markersRef.current.forEach((marker) => {
      marker.setMap(null);
    });
    markersRef.current = [];

    if (eventsWithCoords.length === 0) {
      // No events, center on Istanbul
      const istanbul = getIstanbulCoordinates();
      mapInstance.current.setCenter(istanbul);
      mapInstance.current.setZoom(12);
      return;
    }

    // Use type assertion for extended Google Maps API
    const maps = window.google.maps as unknown as ExtendedGoogleMaps;

    // Create bounds
    const mapBounds = new maps.LatLngBounds();

    // Create new markers
    eventsWithCoords.forEach((event) => {
      const lat = parseFloat(event.locationLatitude!);
      const lng = parseFloat(event.locationLongitude!);
      const position = { lat, lng };

      mapBounds.extend(position);

      const marker = new maps.Marker({
        map: mapInstance.current,
        position: position,
        icon: createPurpleCircleIcon(maps),
        title: event.eventTitle || event.title || 'Event',
      });

      marker.addListener('click', () => {
        const eventUrl = generateEventUrl(event.eventTitle || event.title, event.eventID);
        window.open(eventUrl, '_blank');
      });

      markersRef.current.push(marker);
    });

    // Fit bounds
    if (eventsWithCoords.length > 0) {
      mapInstance.current.fitBounds(mapBounds);
    }
  }, [eventsWithCoords]);

  // Don't show map if online filter is active
  if (isOnlineFilterActive) {
    return null;
  }

  if (loadError) {
    return (
      <div className={styles.mapContainer}>
        <div className={styles.mapError}>{loadError}</div>
      </div>
    );
  }

  return (
    <div className={styles.mapContainer}>
      <div ref={mapRef} className={styles.map} />
    </div>
  );
};

export default EventsMap;
